/**
 * LCP (Largest Contentful Paint) Optimizer
 * Modern approach to identify and optimize the LCP element
 * Uses PerformanceObserver API for accurate LCP detection
 * 
 * @version 2.0.0
 */
(function() {
  'use strict';

  // Early LCP detection and optimization
  function optimizeLCP() {
    // Use PerformanceObserver for accurate LCP tracking
    if ('PerformanceObserver' in window && 'PerformancePaintTiming' in window) {
      try {
        var lcpObserver = new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          var lastEntry = entries[entries.length - 1];
          
          if (lastEntry && lastEntry.element) {
            var lcpElement = lastEntry.element;
            
            // Ensure LCP element has high priority
            if (lcpElement.tagName === 'IMG') {
              lcpElement.setAttribute('fetchpriority', 'high');
              lcpElement.removeAttribute('loading'); // Remove lazy loading if present
              lcpElement.setAttribute('decoding', 'async');
              
              // Preload if not already loaded
              if (!lcpElement.complete) {
                var link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = lcpElement.src || lcpElement.currentSrc;
                link.setAttribute('fetchpriority', 'high');
                document.head.appendChild(link);
              }
            } else if (lcpElement.tagName === 'VIDEO') {
              lcpElement.setAttribute('preload', 'auto');
            } else if (lcpElement.style && lcpElement.style.backgroundImage) {
              // For background images, extract URL and preload
              var bgMatch = lcpElement.style.backgroundImage.match(/url\(['"]?(.*?)['"]?\)/);
              if (bgMatch && bgMatch[1]) {
                var link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = bgMatch[1];
                link.setAttribute('fetchpriority', 'high');
                document.head.appendChild(link);
              }
            }
          }
        });
        
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // Fallback for browsers that don't support PerformanceObserver
        fallbackLCPOptimization();
      }
    } else {
      // Fallback for older browsers
      fallbackLCPOptimization();
    }
  }

  // Fallback LCP optimization for older browsers
  function fallbackLCPOptimization() {
    // Identify likely LCP elements
    var candidates = [
      // Hero images
      document.querySelector('.t4s-hero-image img'),
      document.querySelector('.t4s-hero img'),
      document.querySelector('.kt-hero-enhanced__background'),
      document.querySelector('[class*="hero"] img'),
      
      // First large image in viewport
      document.querySelector('main img'),
      document.querySelector('article img'),
      
      // Product images on product pages
      document.querySelector('.t4s-product-main-img'),
      document.querySelector('.product__media img')
    ].filter(function(el) { return el !== null; });

    // Optimize the first candidate found
    if (candidates.length > 0) {
      var lcpCandidate = candidates[0];
      
      if (lcpCandidate.tagName === 'IMG') {
        lcpCandidate.setAttribute('fetchpriority', 'high');
        lcpCandidate.removeAttribute('loading');
        lcpCandidate.setAttribute('decoding', 'async');
        
        // Preload if not already loaded
        if (!lcpCandidate.complete && lcpCandidate.src) {
          var link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = lcpCandidate.src;
          link.setAttribute('fetchpriority', 'high');
          document.head.appendChild(link);
        }
      } else if (lcpCandidate.style && lcpCandidate.style.backgroundImage) {
        // Preload background image
        var bgMatch = lcpCandidate.style.backgroundImage.match(/url\(['"]?(.*?)['"]?\)/);
        if (bgMatch && bgMatch[1]) {
          var link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = bgMatch[1];
          link.setAttribute('fetchpriority', 'high');
          document.head.appendChild(link);
        }
      }
    }
  }

  // Preload hero images immediately (before DOMContentLoaded)
  // PERFORMANCE: DISABLED - hero_image.liquid already handles synchronous preload
  // This function was redundant and could cause duplicate preloads
  function preloadHeroImages() {
    // DISABLED: hero_image.liquid section already creates preload links synchronously
    // Running this again would be redundant and waste resources
  }

  // Optimize LCP when DOM is ready - but don't block initial render
  // PERFORMANCE: Use requestIdleCallback to avoid blocking LCP measurement
  function initLCPOptimization() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', optimizeLCP, { once: true });
    } else {
      optimizeLCP();
    }
  }
  
  // Defer to avoid blocking LCP
  if ('requestIdleCallback' in window) {
    requestIdleCallback(initLCPOptimization, { timeout: 500 });
  } else {
    setTimeout(initLCPOptimization, 50);
  }

  // CSS deferral is handled exclusively by the inline script in theme.liquid.
  // Running a second pass here after window.load was re-deferring already-active
  // stylesheets back to media="print", permanently stranding product/layout CSS.
  function reduceRenderBlocking() {
    // intentionally empty — theme.liquid MutationObserver handles CSS deferral
  }
  
  // Optimize font loading - but don't modify existing links
  function optimizeFonts() {
    // Fonts are already optimized in head_assets.liquid with display=swap
    // This is just a safety check
    const fontLinks = document.querySelectorAll('link[rel="stylesheet"][href*="fonts.googleapis.com"]:not([href*="display="])');
    
    if (fontLinks.length > 0) {
      fontLinks.forEach(function(link) {
        try {
          const href = link.href;
          if (href && !href.includes('display=')) {
            const url = new URL(href);
            url.searchParams.set('display', 'swap');
            link.href = url.toString();
          }
        } catch (e) {
          // Skip if URL parsing fails
        }
      });
    }
  }
  
  // Initialize render-blocking optimization - but only after page load
  window.addEventListener('load', function() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function() {
        reduceRenderBlocking();
        optimizeFonts();
      }, { timeout: 2000 });
    } else {
      setTimeout(function() {
        reduceRenderBlocking();
        optimizeFonts();
      }, 1000);
    }
  });
  
  // CRITICAL: Monitor LCP element to debug why it's not being detected
  // This helps identify if hero image is actually the LCP element
  function monitorLCP() {
    if (!('PerformanceObserver' in window)) return;
    
    // PERFORMANCE: Verify hero image visibility and loading status immediately
    function verifyHeroImage() {
      const heroImages = document.querySelectorAll('.t4s-hero-image img.t4s-lcp-image, .t4s-hero img[data-lcp-candidate="true"]');
      if (heroImages.length === 0 && window.logger) {
        window.logger.warn('[LCP Monitor] No hero images found with LCP candidate attributes');
        return;
      }
      
      heroImages.forEach(function(img) {
        if (window.logger) {
          const computed = window.getComputedStyle(img);
          const isVisible = computed.opacity !== '0' && 
                           computed.visibility !== 'hidden' && 
                           computed.display !== 'none';
          const isLoaded = img.complete && img.naturalHeight > 0;
          
          window.logger.log('[LCP Monitor] Hero image status:', {
            src: img.src || img.currentSrc || 'N/A',
            visible: isVisible,
            loaded: isLoaded,
            width: img.naturalWidth || 0,
            height: img.naturalHeight || 0,
            opacity: computed.opacity,
            visibility: computed.visibility,
            display: computed.display,
            zIndex: computed.zIndex
          });
          
          if (!isVisible) {
            window.logger.warn('[LCP Monitor] Hero image is NOT visible - this will prevent LCP detection!');
          }
          if (!isLoaded && img.src) {
            window.logger.warn('[LCP Monitor] Hero image not loaded yet:', img.src);
          }
        }
      });
    }
    
    // Verify hero image immediately and after a delay
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(verifyHeroImage, 100);
      }, { once: true });
    } else {
      setTimeout(verifyHeroImage, 100);
    }
    
    try {
      const lcpObserver = new PerformanceObserver(function(list) {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        // Log LCP details for debugging
        if (window.logger) {
          // BUG FIX: Check if value exists before arithmetic to prevent 'NaNs' in output
          var lcpValue = (lastEntry.value && typeof lastEntry.value === 'number') 
            ? (lastEntry.value / 1000).toFixed(2) + 's' 
            : 'N/A';
          
          window.logger.log('[LCP Monitor] LCP detected:', {
            element: lastEntry.element ? lastEntry.element.tagName + (lastEntry.element.className ? '.' + lastEntry.element.className.split(' ')[0] : '') : 'N/A',
            url: lastEntry.url || 'N/A',
            size: lastEntry.size || 0,
            renderTime: lastEntry.renderTime || 0,
            loadTime: lastEntry.loadTime || 0,
            value: lcpValue
          });
        }
        
        // If LCP is not the hero image, log warning
        if (lastEntry.element) {
          const isHeroImage = lastEntry.element.closest('.t4s-hero-image') || 
                             lastEntry.element.closest('.t4s-hero') ||
                             lastEntry.element.classList.contains('t4s-lcp-image');
          
          // PERFORMANCE: Only log in dev mode - suppress in production to reduce console noise
          if (!isHeroImage && window.logger && (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1'))) {
            window.logger.warn('[LCP Monitor] LCP element is NOT hero image:', lastEntry.element);
          }
        }
      });
      
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      // PerformanceObserver not supported or error
      if (window.logger) {
        window.logger.error('[LCP Monitor] Failed to observe LCP:', e);
      }
    }
  }
  
  // Start LCP monitoring immediately
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', monitorLCP, { once: true });
  } else {
    monitorLCP();
  }
  
  // Expose API for manual LCP optimization
  window.KTLCPOptimizer = {
    optimize: optimizeLCP,
    preloadHeroImages: preloadHeroImages,
    reduceRenderBlocking: reduceRenderBlocking,
    optimizeFonts: optimizeFonts,
    monitorLCP: monitorLCP
  };
})();

